import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { getBackgroundImage } from '../../../utils/backgroundImages';

import './style.css';

/**
 * WithBackground - Higher Order Component for adding background images
 *
 * Wraps any component content with a background image layer
 *
 * @param {Object} props
 * @param {string} props.bg - Background image name from backgroundImages mapping
 * @param {string} props.overlay - Overlay type: 'dark', 'light', 'golden', 'none'
 * @param {React.ReactNode} props.children - Component content to wrap
 *
 * @example
 * <WithBackground bg="main" overlay="dark">
 *   <YourComponent />
 * </WithBackground>
 */
const WithBackground = ({ bg, overlay = 'dark', children }) => {
  const backgroundImage = getBackgroundImage(bg);

  if (!backgroundImage) {
    return <>{children}</>;
  }

  return (
    <div className='with-background'>
      <div
        className='with-background__image'
        style={{ backgroundImage: `url(${backgroundImage})` }}
      />
      {overlay !== 'none' && (
        <div
          className={classNames('with-background__overlay', {
            'with-background__overlay--dark': overlay === 'dark',
            'with-background__overlay--light': overlay === 'light',
            'with-background__overlay--golden': overlay === 'golden',
          })}
        />
      )}
      <div className='with-background__content'>{children}</div>
    </div>
  );
};

WithBackground.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
  children: PropTypes.node.isRequired,
};

WithBackground.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default WithBackground;
